import { takeEvery, takeLatest } from "redux-saga/effects";

import { AuthenticationType } from "domains/authentication/actionTypes";
import { unauthenticatedSaga } from "domains/authentication/sagas/logoutSaga";
import { TopicCaseStudyActionTypes } from "domains/caseStudy/actionTypes";
import { fetchCaseStudiesByIdSaga } from "domains/caseStudy/sagas/fetchCaseStudiesByIdSaga";
import { ClusterActionType } from "domains/clusters/actionTypes";
import { clusterItemSaga, clusterKnowledgeSaga } from "domains/clusters/sagas";
import { CoachmarkDomainsActionType } from "domains/coachmarks/actionTypes";
import { changeCoachmarkUsedStatusSaga } from "domains/coachmarks/sagas/changeCoachmarkUsedStatusSaga";
import { fetchCoachmarksVisibilityStatusSaga } from "domains/coachmarks/sagas/fetchCoachmarksVisibilityStatusSaga";
import { fetchUsedCoachmarksSaga } from "domains/coachmarks/sagas/fetchUsedCoachmarksSaga";
import { initSaga } from "domains/coachmarks/sagas/initSaga";
import { toggleCoachmarksSaga } from "domains/coachmarks/sagas/toggleCoachmarksSaga";
import { CollectionActionType } from "domains/collection/actionTypes";
import { createCollectionSaga } from "domains/collection/sagas/createCollectionSaga";
import { deleteCollectionSaga } from "domains/collection/sagas/deleteCollectionSaga";
import { editCommunitySaga } from "domains/collection/sagas/editCommunitySaga";
import { fetchCollectionForEditingSaga } from "domains/collection/sagas/fetchCollectionForEditingSaga";
import { fetchCollectionForViewingSaga } from "domains/collection/sagas/fetchCollectionForViewingSaga";
import { fetchCollectionsSaga } from "domains/collection/sagas/fetchCollectionsSaga";
import { fetchCommunitiesSaga } from "domains/collection/sagas/fetchCommunitiesSaga";
import { fetchCommunitySaga } from "domains/collection/sagas/fetchCommunitySaga";
import { followCollectionSaga } from "domains/collection/sagas/followCollectionSaga";
import { unfollowCollectionSaga } from "domains/collection/sagas/unfollowCollectionSaga";
import { updateCollectionSaga } from "domains/collection/sagas/updateCollectionSaga";
import { uploadImageSaga } from "domains/collection/sagas/uploadImageSaga";
import { CreateMapTypes } from "domains/createMapDetail/actionTypes";
import { getAIGeneratedMapProgressSaga } from "domains/createMapDetail/sagas/getAIGeneratedMapProgressSaga";
import { uploadMapSaga } from "domains/createMapDetail/sagas/uploadMapSaga";
import { MapShareType } from "domains/createShareSettings/actionTypes";
import { acceptMapInviteSaga } from "domains/createShareSettings/sagas/acceptMapInviteSaga";
import { changePermissionSaga } from "domains/createShareSettings/sagas/changePermissionSaga";
import { createInvitationSaga } from "domains/createShareSettings/sagas/createInvitationSaga";
import { createLinkSaga } from "domains/createShareSettings/sagas/createLinkSaga";
import { getAccessListSaga } from "domains/createShareSettings/sagas/getAccessListSaga";
import { openLinkInvitationSaga } from "domains/createShareSettings/sagas/openLinkInvitationSaga";
import { removeInvitationSaga } from "domains/createShareSettings/sagas/removeInvitationSaga";
import { CustomContentAndSourcesTypes } from "domains/customContentAndSources/actionTypes";
import { deleteTopicContentSaga } from "domains/customContentAndSources/sagas/deleteTopicContentSaga";
import { getTopicContentSaga } from "domains/customContentAndSources/sagas/getTopicContentSaga";
import { scrapeTopicContentUrlSaga } from "domains/customContentAndSources/sagas/scrapeTopicContentUrlSaga";
import { uploadTopicContentSaga } from "domains/customContentAndSources/sagas/uploadTopicContentSaga";
import { CustomKeyIssueTypes } from "domains/customKeyIssue/actionTypes";
import { createCustomKeyIssueSaga } from "domains/customKeyIssue/sagas/createCustomKeyIssueSaga";
import { TopicEventsType } from "domains/events/actionTypes";
import { applyEventsSearchCriteriaSaga } from "domains/events/sagas/applyEventsSearchCriteriaSaga";
import { IncidentTypes } from "domains/incidents/constants/actionTypes";
import { fetchUnresolvedIncidentsSaga } from "domains/incidents/sagas/fetchUnresolvedIncidentsSaga";
import { storeDismissedIncidentsSaga } from "domains/incidents/sagas/storeDismissedIncidentsSaga";
import { TopicInitiativeType } from "domains/initiatives/actionTypes";
import { applyInitiativesSearchCriteriaSaga } from "domains/initiatives/sagas/applyInitiativesSearchCriteriaSaga";
import { TopicKnowledgeDataActionTypes } from "domains/knowledgeData/actionTypes";
import { applyKnowledgeDataSearchCriteriaSaga } from "domains/knowledgeData/sagas/applyKnowledgeDataSearchCriteriaSaga";
import { TopicLatestActionTypes } from "domains/knowledgeFeed/actionTypes";
import { applyLatestSearchCriteriaSaga } from "domains/knowledgeFeed/sagas/applyLatestSearchCriteriaSaga";
import { LanguageDomainActionType } from "domains/language/actionTypes";
import { fetchTermsSaga } from "domains/language/sagas/fetchTermsSaga";
import { MembershipEventsTypes } from "domains/membershipEvents/actionTypes";
import { fetchMembershipEventByIdSaga } from "domains/membershipEvents/sagas/fetchMembershipEventByIdSaga";
import { fetchMembershipEventsSaga } from "domains/membershipEvents/sagas/fetchMembershipEventsSaga";
import { EmergingThemesActionType } from "domains/monitor/emergingThemes/actionTypes";
import { emergingThemesSaga } from "domains/monitor/emergingThemes/sagas";
import { LatestKnowledgeActionType } from "domains/monitor/latestKnowledge/actionTypes";
import { latestKnowledgeSaga } from "domains/monitor/latestKnowledge/sagas";
import { SuggestionsActionTypes } from "domains/monitor/suggestions/actionTypes";
import { suggestionsSaga } from "domains/monitor/suggestions/sagas";
import { NotificationActionType } from "domains/notifications/actionTypes";
import { fetchNotificationsSaga } from "domains/notifications/sagas/fetchNotificationsSaga";
import { markAllDoneSaga } from "domains/notifications/sagas/markAllDoneSaga";
import { removeNotificationSaga } from "domains/notifications/sagas/removeNotificationSaga";
import { updateNotificationSaga } from "domains/notifications/sagas/updateNotificationSaga";
import { PinAddRejectActionTypes } from "domains/pinAddReject/actionTypes";
import { approveKnowledgeSaga } from "domains/pinAddReject/sagas/approveKnowledgeSaga";
import { fetchTopicPinnedKnowledgeSaga } from "domains/pinAddReject/sagas/fetchTopicPinnedKnowledgeSaga";
import { fetchTopicsForPinnedKnowledgeSaga } from "domains/pinAddReject/sagas/fetchTopicsForPinnedKnowledgeSaga";
import { rejectTopicKnowledgeSaga } from "domains/pinAddReject/sagas/rejectTopicKnowledgeSaga";
import { updateTopicPinnedKnowledgeSaga } from "domains/pinAddReject/sagas/updateTopicPinnedKnowledgeSaga";
import { TopicPublicationActionTypes } from "domains/publication/actionTypes";
import { applyPublicationSearchCriteriaSaga } from "domains/publication/sagas/applyPublicationSearchCriteriaSaga";
import { TopicStakeholderActionTypes } from "domains/stakeholders/actionTypes";
import { applyStakeholdersSearchCriteriaSaga } from "domains/stakeholders/sagas/applyStakeholdersSearchCriteriaSaga";
import { ThemeType } from "domains/theme/actionTypes";
import { setThemeSaga } from "domains/theme/sagas/setThemeSaga";
import { LegacyTopicActionTypes } from "domains/topic/actionTypes";
import { fetchLegacyTopicBriefingSaga } from "domains/topic/sagas/fetchLegacyTopicBriefingSaga";
import { fetchTopicPresentationSaga } from "domains/topic/sagas/fetchTopicPresentationSaga";
import { TopicDetailType } from "domains/topicDetail/actionTypes";
import { getAIGeneratedMapDetailsSaga } from "domains/topicDetail/sagas/getAIGeneratedMapDetailsSaga";
import { initTopicSaga } from "domains/topicDetail/sagas/initTopicSaga";
import { TopicContentMetaTypes } from "domains/topicSearchFilter/actionTypes";
import { fetchTopicFiltersSaga } from "domains/topicSearchFilter/sagas/fetchTopicContentMetaSaga";
import { TransformationMapActionType } from "domains/transformationMap/actionTypes";
import { setEditModeSaga } from "domains/transformationMap/sagas/setEditModeSaga";
import { updateKeyIssueSaga } from "domains/transformationMap/sagas/updateKeyIssueSaga";
import { updateMapTopicSaga } from "domains/transformationMap/sagas/updateMapTopicSaga";
import { unauthenticatedErrorSaga } from "domains/user/sagas/unauthenticatedErrorSaga";
import { TopicVideoActionTypes } from "domains/videos/actionTypes";
import { applyVideoSearchCriteriaSaga } from "domains/videos/sagas/applyVideoSearchCriteriaSaga";
import { WhatsNewActionType } from "domains/whatsNew/actionTypes";
import { fetchWhatsNewSaga } from "domains/whatsNew/sagas/fetchWhatsNewSaga";

export const domainsSagaSpawners = [
  takeEvery(CoachmarkDomainsActionType.INIT, initSaga),
  takeLatest(CoachmarkDomainsActionType.TOGGLE, toggleCoachmarksSaga),
  takeLatest(
    [CoachmarkDomainsActionType.ADD_TO_USED, CoachmarkDomainsActionType.REMOVE_FROM_USED],
    changeCoachmarkUsedStatusSaga,
  ),
  takeLatest(CoachmarkDomainsActionType.FETCH_USED, fetchUsedCoachmarksSaga),
  takeLatest(CoachmarkDomainsActionType.FETCH_VISIBILITY_STATUS, fetchCoachmarksVisibilityStatusSaga),
  takeLatest(LegacyTopicActionTypes.FETCH_BRIEFING, fetchLegacyTopicBriefingSaga),
  takeLatest(LegacyTopicActionTypes.FETCH_PRESENTATION, fetchTopicPresentationSaga),
  takeEvery(CreateMapTypes.UPLOAD, uploadMapSaga),
  takeEvery(CreateMapTypes.GET_AI_GENERATED_PROGRESS, getAIGeneratedMapProgressSaga),
  takeLatest(LanguageDomainActionType.FETCH, fetchTermsSaga),
  takeLatest(
    [TopicPublicationActionTypes.APPLY_SEARCH_CRITERIA, TopicPublicationActionTypes.CLEAR_SEARCH_CRITERIA],
    applyPublicationSearchCriteriaSaga,
  ),
  takeEvery(TopicCaseStudyActionTypes.FETCH_CASE_STUDIES, fetchCaseStudiesByIdSaga),
  takeLatest(
    [TopicVideoActionTypes.APPLY_SEARCH_CRITERIA, TopicVideoActionTypes.CLEAR_SEARCH_CRITERIA],
    applyVideoSearchCriteriaSaga,
  ),
  takeEvery(TopicKnowledgeDataActionTypes.APPLY_SEARCH_CRITERIA, applyKnowledgeDataSearchCriteriaSaga),
  takeEvery(
    [TopicEventsType.APPLY_SEARCH_CRITERIA, TopicEventsType.CLEAR_SEARCH_CRITERIA],
    applyEventsSearchCriteriaSaga,
  ),
  takeEvery(
    [TopicInitiativeType.APPLY_SEARCH_CRITERIA, TopicInitiativeType.CLEAR_SEARCH_CRITERIA],
    applyInitiativesSearchCriteriaSaga,
  ),
  takeEvery(
    [TopicStakeholderActionTypes.APPLY_SEARCH_CRITERIA, TopicStakeholderActionTypes.CLEAR_SEARCH_CRITERIA],
    applyStakeholdersSearchCriteriaSaga,
  ),
  takeEvery("*" as never, unauthenticatedErrorSaga),
  takeLatest(WhatsNewActionType.FETCH, fetchWhatsNewSaga),
  takeEvery(MapShareType.INVITE, createInvitationSaga),
  takeEvery(MapShareType.ACCESS_LIST, getAccessListSaga),
  takeEvery(MapShareType.REMOVE_INVITE, removeInvitationSaga),
  takeEvery(MapShareType.CHANGE_INVITE_PERMISSION, changePermissionSaga),
  takeEvery(MapShareType.CREATE_LINK, createLinkSaga),
  takeEvery(MapShareType.OPEN_MAP_INVITATION, openLinkInvitationSaga),
  takeEvery(MapShareType.ACCEPT_INVITE, acceptMapInviteSaga),
  takeLatest(IncidentTypes.FETCH_UNRESOLVED, fetchUnresolvedIncidentsSaga),
  takeLatest([IncidentTypes.FETCH_UNRESOLVED_SUCCESS, IncidentTypes.DISMISS_INCIDENT], storeDismissedIncidentsSaga),
  takeLatest(NotificationActionType.FETCH, fetchNotificationsSaga),
  takeEvery(NotificationActionType.UPDATE, updateNotificationSaga),
  takeEvery(NotificationActionType.REMOVE, removeNotificationSaga),
  takeEvery(NotificationActionType.MARK_ALL_DONE, markAllDoneSaga),
  takeLatest(EmergingThemesActionType.FETCH, emergingThemesSaga),
  takeLatest(LatestKnowledgeActionType.FETCH, latestKnowledgeSaga),
  takeEvery(ClusterActionType.FETCH, clusterItemSaga),
  takeEvery(ClusterActionType.FETCH_DIG_DEEPER, clusterKnowledgeSaga),
  takeEvery(SuggestionsActionTypes.SUGGESTIONS_FETCH, suggestionsSaga),
  takeEvery(MembershipEventsTypes.FETCH, fetchMembershipEventsSaga),
  takeEvery(MembershipEventsTypes.FETCH_BY_ID, fetchMembershipEventByIdSaga),
  takeEvery(TransformationMapActionType.SET_EDIT_MODE, setEditModeSaga),
  takeEvery(TransformationMapActionType.UPDATE_TOPIC, updateMapTopicSaga),
  takeEvery(TransformationMapActionType.SET_INNER_NODE, updateKeyIssueSaga),
  takeEvery(TopicDetailType.INIT, initTopicSaga),
  takeEvery(TopicDetailType.GET_AI_GENERATED_DETAILS, getAIGeneratedMapDetailsSaga),
  takeEvery(ThemeType.SET, setThemeSaga),
  takeLatest(AuthenticationType.LOGOUT, unauthenticatedSaga),
  takeEvery(CustomKeyIssueTypes.CREATE, createCustomKeyIssueSaga),
  takeLatest(CustomContentAndSourcesTypes.FETCH_TOPIC_CONTENT_LIST, getTopicContentSaga),
  takeEvery(CustomContentAndSourcesTypes.UPLOAD_CONTENT_ATTACHMENT, uploadTopicContentSaga),
  takeEvery(CustomContentAndSourcesTypes.DELETE_CONTENT, deleteTopicContentSaga),
  takeLatest(CustomContentAndSourcesTypes.SCRAPE_URL, scrapeTopicContentUrlSaga),
  takeLatest(TopicContentMetaTypes.FETCH_CONTENT_META, fetchTopicFiltersSaga),
  takeLatest(
    [TopicLatestActionTypes.APPLY_SEARCH_CRITERIA, TopicLatestActionTypes.CLEAR_SEARCH_CRITERIA],
    applyLatestSearchCriteriaSaga,
  ),
  takeEvery(CollectionActionType.DELETE_COLLECTION, deleteCollectionSaga),
  takeEvery(CollectionActionType.EDIT_COMMUNITY, editCommunitySaga),
  takeEvery(CollectionActionType.FETCH_COLLECTION_FOR_EDITING, fetchCollectionForEditingSaga),
  takeEvery(CollectionActionType.FETCH_COLLECTION_FOR_VIEWING, fetchCollectionForViewingSaga),
  takeEvery(CollectionActionType.FETCH_COLLECTIONS, fetchCollectionsSaga),
  takeEvery(CollectionActionType.FETCH_COMMUNITIES, fetchCommunitiesSaga),
  takeEvery(CollectionActionType.FETCH_COMMUNITY, fetchCommunitySaga),
  takeEvery(CollectionActionType.FOLLOW_COLLECTION, followCollectionSaga),
  takeEvery(CollectionActionType.UNFOLLOW_COLLECTION, unfollowCollectionSaga),
  takeEvery(CollectionActionType.CREATE_COLLECTION, createCollectionSaga),
  takeEvery(CollectionActionType.UPDATE_COLLECTION, updateCollectionSaga),
  takeEvery(CollectionActionType.UPLOAD_IMAGE, uploadImageSaga),
  takeLatest(PinAddRejectActionTypes.FETCH_PINNED_KNOWLEDGE, fetchTopicPinnedKnowledgeSaga),
  takeLatest(PinAddRejectActionTypes.UPDATE_PINNED_KNOWLEDGE, updateTopicPinnedKnowledgeSaga),
  takeLatest(PinAddRejectActionTypes.REJECT_KNOWLEDGE, rejectTopicKnowledgeSaga),
  takeLatest(PinAddRejectActionTypes.APPROVE_KNOWLEDGE, approveKnowledgeSaga),
  takeLatest(PinAddRejectActionTypes.FETCH_TOPICS_FOR_PINNED_KNOWLEDGE, fetchTopicsForPinnedKnowledgeSaga),
];
